import DeleteIcon from '@mui/icons-material/DeleteOutlined';
import { GridRowModes, GridToolbarContainer, GridActionsCellItem, useGridApiRef } from '@mui/x-data-grid-premium';
import { Field, FieldArray, useFormikContext } from 'formik';
import { v4 as uuidv4 } from 'uuid';

import { dataGridPremiumColumns } from '../../utils/helper';

import {
  DataGridIdentifiers,
  FormErrorUI,
  InputUI,
  AutoCompleteSelectUI,
  AddButtonUi,
  TimePickerUi,
  DatagridPremiumUi,
} from '../Interface';
import dayjs from 'dayjs';
import addNotification from '../../utils/addNotification';
import { NotificationType } from '../../config';
import { divide, round } from 'mathjs';
import useParam from '../../context/ParamContext';
import useConfig from '../../context/ConfigContext';
import { useState } from 'react';
import EmployeeAttendanceFieldsDialog from './EmployeeAttendanceFieldsDialog';
import { EmployeeAttendanceSimpleMode } from '../../utils/employeeAttendance';

function EditToolbar(props) {
  const { disabled, push, defaultField, mode, setFieldsDialogData, calculateTimeSpent } = props;
  const { values, validateField } = useFormikContext();
  const { config } = useConfig();

  const handleClick = async () => {
    const newField = {
      ...defaultField,
      id: uuidv4(),
      fromTime: config.defTaskListReportFromTime,
      toTime: config.defTaskListReportToTime,
      timeSpent: calculateTimeSpent(config.defTaskListReportFromTime, config.defTaskListReportToTime),
    };
    await push(newField);

    const fieldsToValidate = [
      `employeeAttendanceLine.${values.employeeAttendanceLine.length ?? 0}.employeeId`,
      `employeeAttendanceLine.${values.employeeAttendanceLine.length ?? 0}.fromTime`,
      `employeeAttendanceLine.${values.employeeAttendanceLine.length ?? 0}.toTime`,
      `employeeAttendanceLine.${values.employeeAttendanceLine.length ?? 0}.rating`,
      `employeeAttendanceLine.${values.employeeAttendanceLine.length ?? 0}.timeSpent`,
    ];

    for (const field of fieldsToValidate) {
      await validateField(field);
    }
  };

  return (
    <GridToolbarContainer>
      {EmployeeAttendanceSimpleMode(mode) ? (
        <AddButtonUi text="Dolgozó hozzáadása" onClick={() => setFieldsDialogData({ open: true })} />
      ) : (
        <AddButtonUi disabled={disabled} onClick={handleClick} />
      )}
    </GridToolbarContainer>
  );
}

const EmployeeAttendanceLine = ({ disabled, defaultField, autocompleteOptions, mode }) => {
  const apiRef = useGridApiRef();
  const { params } = useParam();
  const { values, validateField, setFieldValue, errors } = useFormikContext();
  const defFieldsData = {
    open: false,
    index: null,
  };
  const [fieldsDialogData, setFieldsDialogData] = useState(defFieldsData);

  const initialState = {
    filter: { filterModel: { items: [], logicOperator: 'and' } },
    sorting: {
      sortModel: [],
    },
    pagination: {
      paginationModel: {
        pageSize: 25,
        page: 0,
      },
    },
    density: 'compact',
  };

  const getValueIndex = (values, id) => {
    return values.employeeAttendanceLine.findIndex((rc) => rc.id === id);
  };

  const calculateTimeSpent = (fromTime, toTime) => {
    if (!fromTime || !toTime) return null;

    const from = dayjs.utc(fromTime);
    const to = dayjs.utc(toTime);

    return round(to.diff(from, 'hour', true), 2);
  };

  const columns = (setFieldValue, validateField, errors, remove) => {
    return dataGridPremiumColumns([
      {
        field: 'employeeId',
        headerName: 'Név',
        editable: true,
        sortable: false,
        type: 'string',
        renderCell: (data) => {
          const findedEmployee = autocompleteOptions?.employees.find((item) => item.value === data.row?.employeeId);
          return findedEmployee?.label;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const findedEmployee = autocompleteOptions.employees.find((item) => item.value === data.row.employeeId);

          return (
            <Field
              name={`employeeAttendanceLine.${valueIndex}.employeeId`}
              component={AutoCompleteSelectUI}
              helperText={<FormErrorUI message={errors?.employeeAttendanceLine?.[valueIndex]?.employeeId} />}
              selectedValue={values.employeeAttendanceLine?.[valueIndex]?.employeeId}
              selectedLabelValue={findedEmployee?.label}
              onChange={(_e, newVal) => {
                setFieldValue(`employeeAttendanceLine.${valueIndex}.employeeId`, newVal?.value ?? null);
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: newVal.value ?? null,
                });
              }}
              dataset={autocompleteOptions.employees}
              onBlur={async () => await validateField(`employeeAttendanceLine.${valueIndex}.employeeId`)}
            />
          );
        },
      },
      {
        field: 'fromTime',
        headerName: 'Kezdő időpont',
        type: 'time',
        editable: true,
        valueGetter: (_data, row) => {
          return row.fromTime ? new Date(row.fromTime) : null;
        },
        renderCell: (data) => {
          if (!data.row.fromTime) return null;
          return dayjs.utc(data.row.fromTime).format('HH:mm');
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const currentRow = values.employeeAttendanceLine?.[valueIndex];

          return (
            <Field
              name={`employeeAttendanceLine.${valueIndex}.fromTime`}
              component={TimePickerUi}
              timeSteps={{ minutes: 15 }}
              helperText={<FormErrorUI message={errors?.employeeAttendanceLine?.[valueIndex]?.fromTime} />}
              variant="standard"
              value={currentRow?.fromTime ? dayjs.utc(currentRow?.fromTime) : null}
              onChange={(newDate) => {
                if (!newDate) {
                  setFieldValue(`employeeAttendanceLine.${valueIndex}.fromTime`, null);
                  setFieldValue(`employeeAttendanceLine.${valueIndex}.timeSpent`, null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: null,
                  });
                  return;
                }
                const iso = dayjs.utc(`1970-01-01T${newDate.format('HH:mm')}:00`).toISOString();

                const newTimeSpent = calculateTimeSpent(iso, currentRow?.toTime);

                setFieldValue(`employeeAttendanceLine.${valueIndex}.fromTime`, iso);
                setFieldValue(`employeeAttendanceLine.${valueIndex}.timeSpent`, newTimeSpent);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: iso,
                });
              }}
              onBlur={async () => await validateField(`employeeAttendanceLine.${valueIndex}.fromTime`)}
            />
          );
        },
      },
      {
        field: 'toTime',
        headerName: 'Befejező időpont',
        editable: true,
        type: 'time',
        valueGetter: (_data, row) => {
          return row.toTime ? new Date(row.toTime) : null;
        },
        renderCell: (data) => {
          if (!data.row.toTime) return null;
          return dayjs.utc(data.row.toTime).format('HH:mm');
        },

        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const value = values.employeeAttendanceLine?.[valueIndex]?.toTime;

          return (
            <Field
              name={`employeeAttendanceLine.${valueIndex}.toTime`}
              component={TimePickerUi}
              helperText={<FormErrorUI message={errors?.employeeAttendanceLine?.[valueIndex]?.toTime} />}
              variant="standard"
              timeSteps={{ minutes: 15 }}
              value={value ? dayjs.utc(value) : null}
              onChange={(newDate) => {
                if (!newDate) {
                  setFieldValue(`employeeAttendanceLine.${valueIndex}.toTime`, null);
                  setFieldValue(`employeeAttendanceLine.${valueIndex}.timeSpent`, null);
                  data.api.setEditCellValue({
                    id: data.id,
                    field: data.field,
                    value: null,
                  });
                  return;
                }

                const fromRaw = values.employeeAttendanceLine?.[valueIndex]?.fromTime;
                const to = dayjs.utc(`1970-01-01T${newDate.format('HH:mm')}:00`);

                if (fromRaw) {
                  const from = dayjs.utc(fromRaw);

                  if (to.isBefore(from)) {
                    addNotification({
                      content: 'A befejező időpont nem lehet korábbi, mint a kezdő időpont!',
                      type: NotificationType.WARNING,
                    });
                    return;
                  }
                }

                const iso = to.toISOString();
                const newTimeSpent = calculateTimeSpent(fromRaw, iso);

                setFieldValue(`employeeAttendanceLine.${valueIndex}.toTime`, iso);
                setFieldValue(`employeeAttendanceLine.${valueIndex}.timeSpent`, newTimeSpent);

                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: iso,
                });
              }}
              onBlur={async () => await validateField(`employeeAttendanceLine.${valueIndex}.toTime`)}
            />
          );
        },
      },
      ...(params.SHOW_EMPLOYEE_ATTENDANCE_RATING_FIELD === 'Y'
        ? [
            {
              field: 'rating',
              headerName: 'Értékelés',
              type: 'number',
              editable: true,
              sortable: false,
              renderEditCell: (data) => {
                const valueIndex = getValueIndex(values, data.id);

                return (
                  <Field
                    name={`employeeAttendanceLine.${valueIndex}.rating`}
                    type="number"
                    variant="standard"
                    as={InputUI}
                    fullWidth
                    helperText={<FormErrorUI message={errors?.employeeAttendanceLine?.[valueIndex]?.rating} />}
                    onChange={(e) => {
                      setFieldValue(`employeeAttendanceLine.${valueIndex}.rating`, e.target.value ?? '');
                      data.api.setEditCellValue({
                        id: data.id,
                        field: data.field,
                        value: e.target.value,
                      });
                    }}
                    onBlur={async () => await validateField(`employeeAttendanceLine.${valueIndex}.rating`)}
                  />
                );
              },
            },
          ]
        : []),
      {
        field: 'timeSpent',
        headerName: 'Idő(óra)',
        type: 'number',
        editable: true,
        sortable: false,
        renderCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);
          const value = values.employeeAttendanceLine?.[valueIndex]?.timeSpent;
          return value ? parseFloat(value) : null;
        },
        renderEditCell: (data) => {
          const valueIndex = getValueIndex(values, data.id);

          return (
            <Field
              name={`employeeAttendanceLine.${valueIndex}.timeSpent`}
              type="number"
              variant="standard"
              as={InputUI}
              fullWidth
              helperText={<FormErrorUI message={errors?.employeeAttendanceLine?.[valueIndex]?.timeSpent} />}
              onChange={(e) => {
                setFieldValue(`employeeAttendanceLine.${valueIndex}.timeSpent`, e.target.value ?? '');
                data.api.setEditCellValue({
                  id: data.id,
                  field: data.field,
                  value: e.target.value,
                });
              }}
              onBlur={async () => await validateField(`employeeAttendanceLine.${valueIndex}.timeSpent`)}
            />
          );
        },
      },
      {
        field: 'actions',
        type: 'actions',
        headerName: 'Szerk.',
        cellClassName: 'actions',
        sortable: false,
        getActions: ({ id, row }) => {
          return [
            <GridActionsCellItem
              icon={<DeleteIcon />}
              label="Delete"
              onClick={(e) => {
                e.stopPropagation();
                const valueIndex = getValueIndex(values, id);
                return remove(valueIndex);
              }}
              color="inherit"
              disabled={disabled && !EmployeeAttendanceSimpleMode(mode)}
            />,
          ];
        },
      },
    ]);
  };

  return (
    <>
      <div className="overflow-x-auto">
        <div className="align-middle">
          <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 sm:rounded-lg">
            <FieldArray name="employeeAttendanceLine">
              {({ remove, push }) => (
                <DatagridPremiumUi
                  apiRef={apiRef}
                  datagridInitialState={initialState}
                  datagridIdentifier={DataGridIdentifiers.EmployeeAttendanceLine}
                  height={460}
                  columns={columns(setFieldValue, validateField, errors, remove)}
                  rows={values?.employeeAttendanceLine ?? []}
                  headerFilters={false}
                  slots={{
                    toolbar: EditToolbar,
                  }}
                  getRowHeight={() => 'auto'}
                  slotProps={{
                    toolbar: {
                      disabled,
                      push,
                      defaultField,
                      mode,
                      setFieldsDialogData,
                      calculateTimeSpent,
                    },
                  }}
                  editMode="row"
                  onCellDoubleClick={(_params, event) => {
                    event.defaultMuiPrevented = true;
                  }}
                  onCellClick={(params, event) => {
                    if (disabled && !EmployeeAttendanceSimpleMode(mode)) return;

                    if (EmployeeAttendanceSimpleMode(mode)) {
                      const valueIndex = getValueIndex(values, params.row.id);

                      setFieldsDialogData({ open: true, index: valueIndex });
                    } else {
                      const rowMode = apiRef.current.getRowMode(params.row.id);
                      if (rowMode === GridRowModes.View) apiRef.current.startRowEditMode({ id: params.row.id });
                    }
                  }}
                />
              )}
            </FieldArray>
          </div>
        </div>
      </div>
      <EmployeeAttendanceFieldsDialog
        dialogData={fieldsDialogData}
        onClose={() => setFieldsDialogData(defFieldsData)}
        autocompleteOptions={autocompleteOptions}
        defaultField={defaultField}
      />
    </>
  );
};

export default EmployeeAttendanceLine;
